import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Modal,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Popover,
  TextField,
  InputAdornment,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from '@mui/icons-material/Person';
import QRCodeStyling from "qr-code-styling";
import {
  getSingleEvent,
  getAttendeeAccessPass,
  transferTicket,
  cancelTicket,
} from "../../../../Redux/Reducers/communityReducer";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import VideoCameraFrontIcon from "@mui/icons-material/VideoCameraFront";
import SearchIcon from '@mui/icons-material/Search';
import { useUserDetailContext } from "../../../UserDetailContext";
import { useQuantityContext } from "../../../QuantityContext";
import TicketSkeletonLoading from "../../../LoadingSkeleton/TicketSkeletonLoading";
import debounce from "lodash/debounce"; 
import { getGlobleUsers } from "../../../../Redux/Reducers/ContactReducer";

const DEFAULT_IMAGE = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTJUt3PE61yxQt2pJlLy5FJUP6ui46BBHmnFotsysgdKKY9pQsMc9oRkoqk2Sr--qdpbuk&usqp=CAU";

const TicketCard = () => {
  const { userDetails } = useUserDetailContext();
  const qrRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [flipped, setFlipped] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { openSnackbar } = useQuantityContext();
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    candidate: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const attendeeAcessPass = useSelector(
    (state) => state.community.attendeeAcessPass
  );

  const { event, attendee, ticket_id, groupDetails } = attendeeAcessPass;
  const [qrValue, setQrValue] = useState(`${ticket_id}|${Math.floor(Date.now() / 1000)}`);
  const [openGroupModal, setOpenGroupModal] = useState(false);
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  
  // Replace interviewers with globalusersData
  const globalusersData = useSelector(state => state.ContactArray?.globalusersData?.data) || [];
  
  console.log("globalusersDataMMM", globalusersData);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchValue) => {
      const params = {
        searchParam: searchValue.trim() === "" ? "a" : searchValue,
        limit: 20,
        offsetValue: 1,
      };
      await dispatch(getGlobleUsers(params));
      setIsSearching(false);
    }, 500),
    [dispatch]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsSearching(true);
    debouncedSearch(value);
  };

  useEffect(() => {
    // Initialize loading state
    if (attendeeAcessPass) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [attendeeAcessPass]);

  useEffect(() => {
    // Update QR value every 5 seconds
    const interval = setInterval(() => {
      setQrValue(`${ticket_id}|${Math.floor(Date.now() / 1000)}`);
    }, 5000);
    return () => clearInterval(interval);
  }, [ticket_id]);

  useEffect(() => {
    setQrValue(`${ticket_id}`);
  }, [ticket_id]);

  // QRCodeStyling instance
  const qrCode = useRef(
    new QRCodeStyling({
      width: 140,
      height: 140,
      type: "svg",
      data: qrValue,
      image: "",
      dotsOptions: {
        color: "#253A7D",
        type: "rounded"
      },
      backgroundOptions: {
        color: "#fff"
      }
    })
  );

  // Format date and time
  const eventDate = event?.start_time ? new Date(event.start_time) : new Date();
  const formattedDate = eventDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const formattedTime = eventDate.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Format duration
  const durationInMinutes = event?.duration_in_minutes || 0;
  let durationText = "";
  if (durationInMinutes) {
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;
    if (hours === 0) {
      durationText = `${minutes} mins`;
    } else if (minutes === 0) {
      durationText = `${hours} hr${hours !== 1 ? "s" : ""}`;
    } else {
      durationText = `${hours} hr${hours !== 1 ? "s" : ""} ${minutes} min${minutes !== 1 ? "s" : ""
        }`;
    }
  }

  const imageUrl = event?.feature_image || DEFAULT_IMAGE;

  // Render QR code on value change
  useEffect(() => {
    qrCode.current.update({ data: qrValue });
    if (qrRef.current) {
      qrRef.current.innerHTML = "";
      qrCode.current.append(qrRef.current);
    }
  }, [qrValue]);

  const handleOpenGroupModal = () => {
    setOpenGroupModal(true);
  };

  const handleCloseGroupModal = () => {
    setOpenGroupModal(false);
  };

  const handleMoreClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const handleMemberSelect = async (newTicketId) => {
    if (newTicketId !== ticket_id) {
      setIsLoading(true);
      const result = await dispatch(getAttendeeAccessPass(newTicketId));
      if (result?.payload?.success) {
        setIsLoading(false);
        console.log("API call successful with ticket id:", newTicketId);
      } else {
        setIsLoading(false);
        console.error("API call failed");
      }
    }
    handleCloseGroupModal();
  };

  const hadleTrnsferModalOpen = async () => {
    // Initial load of global users
    setIsLoading(true);
    const params = {
      searchParam: "a", // Initial search param to get users
      limit: 20,
      offsetValue: 1,
    };
    await dispatch(getGlobleUsers(params));
    setIsLoading(false);
    setOpenTransferModal(true);
    setSearchTerm(""); // Reset search term when opening modal
    handleMoreClose();
  };

  const handleCloseTransferModal = () => {
    setOpenTransferModal(false);
    setSearchTerm(""); // Reset search term when closing modal
  };

  // Handler for clicking a candidate in the transfer modal
  const handleCandidateClick = (candidate) => {
    setConfirmModal({ open: true, candidate });
  };

  // Handler for confirming transfer
  const handleConfirmTransfer = async () => {
    if (confirmModal?.candidate) {
      const payload = {
        ticketId: ticket_id,
        payload: {
          new_attendee_id: confirmModal?.candidate?.reference_id
        }
      };

      try {
        setIsLoading(true);
        await dispatch(transferTicket(payload));
        setIsLoading(false);
        setConfirmModal({ open: false, candidate: null });
        openSnackbar("Ticket transferred successfully", "success", 4000);
        setOpenTransferModal(false);
      } catch (error) {
        setIsLoading(false);
        console.error("Ticket transfer failed:", error);
        openSnackbar("Ticket transfer failed. Please try again.", "error", 4000);
      }
    }
  };

  // Handler for closing confirmation modal
  const handleCloseConfirmModal = () => {
    setConfirmModal({ open: false, candidate: null });
  };

  const handlecancelTicket = () => {
    setCancelModal(true);
  }

  const handleConfirmCancelTicket = async () => {
    setIsLoading(true);
    await dispatch(cancelTicket({ ticketId: ticket_id }));
    setIsLoading(false);
    setCancelModal(false);
    setAnchorEl(null);
    handleCloseCancelModal()
  };

  const handleCloseCancelModal = () => {
    setCancelModal(false);
  };

  if (!attendeeAcessPass || isLoading) {
    return (
      <TicketSkeletonLoading />
    );
  }

  return (
    <div
      className={`ticket-flip-container${flipped ? " flipped" : ""}`}
      onClick={e => {
        // Prevent flip if click is on or inside a MoreVert menu
        let el = e.target;
        while (el) {
          if (el.getAttribute && el.getAttribute('data-morevert') === 'true') return;
          el = el.parentElement;
        }
        setFlipped(f => !f);
      }}
      style={{
        cursor: "pointer",
        maxWidth: 370,
        width: "100%",
        margin: "0 auto",
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      }}
      title="Click to flip"
    >
      <style jsx>{`
        .ticket-flip-container {
          perspective: 1200px;
          min-height: 600px;
          transition: box-shadow 0.3s, transform 0.3s;
        }
        .ticket-flip-container.flipped .ticket-flip-inner {
          transform: rotateY(180deg);
        }
        .ticket-flip-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.6s cubic-bezier(.4,2,.6,1);
          transform-style: preserve-3d;
        }
        .ticket-flip-front, .ticket-flip-back {
          position: absolute;
          width: 100%;
          height: 100%;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          left: 0;
          top: 0;
        }
        .ticket-flip-back {
          transform: rotateY(180deg);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-radius: 24px;
          padding: 32px 16px;
        }
      `}</style>

      <div className="ticket-flip-inner">
        {/* FRONT SIDE */}
        <div className="ticket-flip-front">
          <Box
            sx={{
              borderRadius: "24px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
              overflow: "hidden",
              background: "#fff",
              minHeight: 540,
              height: 540,
              width: "100%",
              pb: 1,
              position: "relative",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #253A7D 0%, #3B5998 100%)",
                color: "#fff",
                px: 2,
                pt: 2,
                pb: 0.5,
                borderTopLeftRadius: "24px",
                borderTopRightRadius: "24px",
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography fontWeight={700} fontSize={16} letterSpacing={1} sx={{ mb: 0.2 }}>
                  EVENT TICKET
                </Typography>
                <Typography fontSize={10} sx={{ opacity: 0.85 }}>
                  {event?.access_level === "Private" ? "Private Event" : event?.access_level || "Event"}
                </Typography>
              </Box>
              <Box>
                {userDetails?.reference_id == attendeeAcessPass?.registered_by?.user_ref_id && groupDetails?.member_count > 1 ? (
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: "15px", color: "white", fontSize: "small" }}
                    onClick={e => {
                      e.stopPropagation();
                      handleOpenGroupModal();
                    }}
                  >
                    Group of {groupDetails?.member_count}
                  </Button>
                ) : (
                  <Box sx={{ position: "absolute", top: 25, right: 25, display: "flex", gap: 0.5 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", opacity: 0.4 }} />
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", opacity: 0.4 }} />
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", opacity: 0.7 }} />
                  </Box>
                )}
              </Box>
            </Box>
            {/* Banner Image with Overlay */}
            <Box
              sx={{
                width: "100%",
                height: 140,
                background: `url(${imageUrl}) center center/cover no-repeat`,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "linear-gradient(180deg,rgba(43,59,120,0.12) 0%,rgba(255,255,255,0.0) 100%)",
                  zIndex: 1,
                }}
              />
            </Box>
            {/* Centered Avatar overlapping banner */}
            <Box sx={{
              display: "flex",
              justifyContent: "left",
              position: "relative",
              zIndex: 2,
              mt: -6,
              ml: 2,
            }}>
              <Avatar
                src={attendee?.profile_url || 'https://www.shutterstock.com/image-vector/vector-flat-illustration-grayscale-avatar-600nw-2281862025.jpg'}
                alt={attendee?.name}
                sx={{
                  width: 90,
                  height: 90,
                  border: "2px solid #0044C6",
                  boxShadow: "0 2px 8px rgba(44,62,80,0.15)",
                  background: "#fff",
                }}
              >
                {attendee?.name?.charAt(0) || "A"}
              </Avatar>
            </Box>
            {/* Attendee Info */}
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "left",
              mt: 1,
              px: 2,
            }}>
              <Typography fontWeight={700} fontSize={18} sx={{ mb: 0.2 }}>
                {attendee?.name || "N/A"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", fontSize: 14, color: "#757575", mb: 0.2 }}>
                <EmailIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {attendee?.email || "N/A"}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", fontSize: 14, color: "#757575" }}>
                <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {attendee?.phone || "N/A"}
              </Box>
            </Box>
            {/* Event Title & Free Chip */}
            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2,
              px: 2,
            }}>
              <Typography fontWeight={700} fontSize={18} sx={{ flex: 1, mr: 1 }}>
                {event?.title || "Event Name"}
              </Typography>
              <Chip
                label={attendeeAcessPass?.attendance_status}
                sx={{
                  color: "#fff",
                  background: "#3B5998",
                  fontWeight: 600,
                  fontSize: 14,
                  borderRadius: 2,
                  px: 1.5,
                  height: 26,
                  ml: 1,
                }}
              />
            </Box>
            {/* Event Info */}
            <Box sx={{ px: 2, mt: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", color: "#222", mb: 1 }}>
                  {event?.mode == "Offline" ? (
                    <LocationOnIcon sx={{ fontSize: 18, mr: 1, color: "#3B5998" }} />
                  ) : (
                    <VideoCameraFrontIcon sx={{ fontSize: 18, mr: 1, color: "#3B5998" }} />
                  )}

                  {event?.mode == "Offline" ? (
                    <Typography
                      component="a"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        event?.event_location
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: 13,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textDecoration: "none",
                        color: "#222",
                        '&:hover': {
                          textDecoration: "underline",
                          color: "#3B5998"
                        }
                      }}
                    >
                      {event?.event_location}
                    </Typography>
                  ) : (
                    (() => {
                      const now = new Date();
                      const eventStart = event?.start_time ? new Date(event.start_time) : null;
                      const minutesUntilEvent = eventStart ? (eventStart - now) / (1000 * 60) : null;
                      const isLinkEnabled = minutesUntilEvent !== null && minutesUntilEvent <= 10;
                      return isLinkEnabled ? (
                        <Typography
                          component="a"
                          href={event?.event_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: 13,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textDecoration: "none",
                            color: "#3B5998",
                            fontWeight: 500,
                            '&:hover': {
                              textDecoration: "underline",
                              color: "#253A7D"
                            }
                          }}
                        >
                          Join Meeting Link
                        </Typography>
                      ) : (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#aaa",
                              cursor: "not-allowed",
                              userSelect: "none",
                            }}
                          >
                            Join Meeting Link
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "#e57373",
                              mt: 0.3,
                            }}
                          >
                            {minutesUntilEvent !== null
                              ? `Link opens 10 min before the event${
                                  minutesUntilEvent > 60
                                    ? ` (starts in ${Math.floor(minutesUntilEvent / 60)}h ${Math.floor(minutesUntilEvent % 60)}m)`
                                    : ` (starts in ${Math.ceil(minutesUntilEvent)} min)`
                                }`
                              : "Meeting link not available"}
                          </Typography>
                        </Box>
                      );
                    })()
                  )}
                </Box>
            

              <Box sx={{ display: "flex", alignItems: "center", fontSize: 13, color: "#222", mb: 1.5 }}>
                <EventIcon sx={{ fontSize: 18, mr: 1, color: "#3B5998" }} />
                {formattedDate} · {formattedTime}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", fontSize: 13, color: "#222", mb: 1.5 }}>
                <AccessTimeIcon sx={{ fontSize: 18, mr: 1, color: "#3B5998" }} />
                Duration: {durationText}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", fontSize: 13, color: "#222", mb: 1.5 }}>
                <ConfirmationNumberIcon sx={{ fontSize: 18, mr: 1, color: "#3B5998" }} />
                Ticket ID: {ticket_id || "NE0100000051"}
              </Box>
            </Box>
          </Box>
        </div>

        {/* BACK SIDE */}
        <div className="ticket-flip-back" >
          <Box
            sx={{
              borderRadius: "24px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
              overflow: "hidden",
              background: "#fff",
              width: "100%",
              maxWidth: 370,
              fontFamily: "inherit",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              pb: 2
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #253A7D 0%, #3B5998 100%)",
                color: "#fff",
                px: 2,
                pt: 2,
                pb: 0.5,
                borderTopLeftRadius: "24px",
                borderTopRightRadius: "24px",
                position: "relative",
              }}
            >
              <Box>
                <Typography fontWeight={700} fontSize={16} letterSpacing={1} sx={{ mb: 0.2 }}>
                  TICKET VERIFICATION
                </Typography>
                <Typography fontSize={10} sx={{ opacity: 0.85 }}>
                  Scan QR Code
                </Typography>
              </Box>

              <Box>
                {attendeeAcessPass?.attendance_status == 'PENDING' && userDetails?.reference_id != attendee?.user_ref_id ? (
                  <Box
                    onClick={handleMoreClick}
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.2)"
                      }
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: "30px", cursor: "pointer", color: "white" }} />
                  </Box>
                ) :
                  <Box sx={{ position: "absolute", top: 25, right: 25, display: "flex", gap: 0.5 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", opacity: 0.4 }} />
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", opacity: 0.4 }} />
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", opacity: 0.7 }} />
                  </Box>
                }
              </Box>
            </Box>
            {/* QR Code Section */}
            <Box
              sx={{
                background: "#fff",
                borderRadius: 3,
                boxShadow: 1,
                mx: "auto",
                mt: 1.5,
                mb: 2,
                width: 180,
                height: 180,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pb: 0,
                zIndex: 1,
              }}
            >
              <div ref={qrRef} style={{ width: 140, height: 140, margin: "0 auto" }} />
              <Typography fontSize={10} color="#222" mt={0.5} textAlign="center">
                Scan at entrance for verification
              </Typography>
            </Box>
            {/* Details Section */}
            <Box sx={{ px: 2, mt: 0.5, mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
                <EventIcon sx={{ fontSize: 18, color: "#3B5998", mr: 1, mt: "2px" }} />
                <Box>
                  <Typography fontSize={12} color="#757575" fontWeight={500}>Event</Typography>
                  <Typography fontSize={14} color="#222" fontWeight={600}>{event?.title || "Event test"}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
                <ConfirmationNumberIcon sx={{ fontSize: 18, color: "#3B5998", mr: 1, mt: "2px" }} />
                <Box>
                  <Typography fontSize={12} color="#757575" fontWeight={500}>Ticket ID</Typography>
                  <Typography fontSize={14} color="#222" fontWeight={700}>{ticket_id || "NE0100000051"}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
                <PersonIcon sx={{ fontSize: 18, color: "#3B5998", mr: 1, mt: "2px" }} />
                <Box>
                  <Typography fontSize={12} color="#757575" fontWeight={500}>Booked By</Typography>
                  <Typography fontSize={14} color="#222" fontWeight={700}>{attendeeAcessPass?.registered_by?.name || "User"}</Typography>
                </Box>
              </Box>
            </Box>
            {/* View Event Details Button */}
            <Box sx={{ px: 2, width: "100%", display: "flex", justifyContent: "center", mt: 1 }}>
              <button
                style={{
                  background: "linear-gradient(90deg, #253A7D 0%, #3B5998 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 32,
                  fontWeight: 700,
                  fontSize: 16,
                  padding: "12px 0",
                  width: "100%",
                  maxWidth: 240,
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(44,62,80,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "background 0.2s",
                  letterSpacing: 1,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(getSingleEvent(event?.id));
                  navigate(`/explore/events/${event?.id}`)
                }}
              >
                View Event Details <span style={{ fontSize: 18, marginLeft: 6 }}>→</span>
              </button>
            </Box>
          </Box>

          <Modal
            open={openGroupModal}
            onClose={handleCloseGroupModal}
            aria-labelledby="group-members-modal"
            aria-describedby="group-members-list"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: '8px'
            }}>
              <Typography variant="h6" component="h2" gutterBottom>
                Group Members
              </Typography>
              <List>
                {groupDetails?.members?.map((member, index) => (
                  <ListItem
                    button
                    key={index}
                    onClick={e => {
                      e.stopPropagation();
                      handleMemberSelect(member?.ticket?.ticket_id);
                    }}
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                  >
                    <ListItemAvatar>
                      <Avatar alt={member?.name} src={member?.profile_url} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={member?.name}
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                            display="block"
                          >
                            {member?.designation} {member?.company && `at ${member?.company}`}
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            display="block"
                          >
                            {member?.email}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Modal>
        </div>
      </div>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMoreClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 1, minWidth: 200 }}>
          <Button
            fullWidth
            onClick={hadleTrnsferModalOpen}
            sx={{ justifyContent: "flex-start", textTransform: "none" }}
          >
            Transfer Ticket
          </Button>
          <Button
            fullWidth
            color="error"
            onClick={handlecancelTicket}
            sx={{ justifyContent: "flex-start", textTransform: "none" }}
          >
            Cancel Ticket
          </Button>
        </Box>
      </Popover>

      {/* Transfer Modal with Search Bar */}
      <Modal
        open={openTransferModal}
        onClose={handleCloseTransferModal}
        aria-labelledby="transfer-candidates-modal"
        aria-describedby="transfer-candidates-list"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 450,
          maxHeight: "80vh",
          overflowY: "auto",
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: '8px',
          position: 'relative'
        }}>
          {/* Close Icon */}
          <CloseIcon
            onClick={handleCloseTransferModal}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              cursor: 'pointer',
              color: '#888',
              '&:hover': { color: '#253A7D' }
            }}
            fontSize="medium"
          />
          <Typography variant="h6" component="h2" gutterBottom>
            Transfer Ticket
          </Typography>
          {/* Search Bar */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <List>
            {isSearching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <Typography>Searching...</Typography>
              </Box>
            ) : globalusersData && globalusersData?.length > 0 ? (
              globalusersData?.map((candidate, idx) => {
                // Skip current user
                if (candidate?.reference_id === attendee?.user_ref_id) {
                  return null;
                }
                return (
                  <ListItem 
                    key={idx} 
                    sx={{ 
                      '&:hover': { backgroundColor: '#f5f5f5' }, 
                      cursor: 'pointer',
                      borderRadius: 1,
                      mb: 0.5
                    }}
                    onClick={() => handleCandidateClick(candidate)}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        alt={candidate?.first_name || candidate?.name} 
                        src={candidate?.profile_url} 
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${candidate?.first_name || candidate?.name || ''} ${candidate?.last_name || ''}`.trim()}
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                            display="block"
                          >
                            {candidate?.email}
                          </Typography>
                          {candidate?.designation && (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              display="block"
                            >
                              {candidate?.designation} {candidate?.company && `at ${candidate?.company}`}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography>No candidates found.</Typography>
              </Box>
            )}
          </List>
        </Box>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        open={confirmModal?.open}
        onClose={handleCloseConfirmModal}
        aria-labelledby="confirm-transfer-modal"
        aria-describedby="confirm-transfer-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 340,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ fontSize: "16px" }}>
            Are you sure you want to transfer the ticket to <b>{confirmModal?.candidate?.first_name || confirmModal?.candidate?.name} {confirmModal?.candidate?.last_name || ''}</b>?
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant="outlined" color="secondary" onClick={handleCloseConfirmModal}>
              No
            </Button>
            <Button variant="contained" color="primary" onClick={handleConfirmTransfer}>
              Yes
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={cancelModal}
        onClose={handleCloseCancelModal}
        aria-labelledby="cancel-ticket-modal"
        aria-describedby="cancel-ticket-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 340,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ fontSize: "16px" }}>
            Are you sure you want to cancel the ticket?
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant="outlined" color="secondary" onClick={handleCloseCancelModal}>
              No
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmCancelTicket}>
              Yes
            </Button>
          </Box>
        </Box>
      </Modal>

    </div>
  );
};

export default TicketCard;